import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { debounce, flatMap } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import withNavbar from '../../core/handlers/withNavbar';
import api from 'src/app/core/api/apiProvider';
import SearchBar from '../components/SearchBar';
import CardGrid from '../components/CardGrid';
import Favorites from '../components/Favorites/handlers/FavoritesRow';
import { List } from '../components/CardList/CardList';
import NewsTicker from 'src/app/ui/NewsTicker';
import {
  ContinueWatchingItem,
  getContinueWatchingItems,
  removeContinueWatchingItem,
  YOUTUBE_CONTINUE_WATCHING_UPDATED,
} from '../utils/youtubeContinueWatching';
import useFilterCategories from '../hooks/useFilterCategories';
import {
  getCategoryLabel,
  getFilterCategory,
  matchesFilterCategory,
} from '../utils/filterCategories';

const APP_LANGUAGE_KEY = 'appLanguage';

const StyledListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  margin: 0 0 5vh;
`;

const Browser = () => {
  const language =
    localStorage.getItem(APP_LANGUAGE_KEY) === 'en' ? 'en' : 'es';

  const copy =
    language === 'en'
      ? {
          continueWatching: 'Continue watching',
          mandatory: 'Mandatory Content',
          aligned: 'Aligned with your Digital DNA',
          internal: 'Shared internal contents',
          latest: 'Latest content',
          youMayLike: 'You might be interested',
          recommendations: 'Recommendations',
          filteredContent: 'Filtered Content',
        }
      : {
          continueWatching: 'Seguir viendo',
          mandatory: 'Contenido Mandatorio',
          aligned: 'Alineado con tu ADN Digital',
          internal: 'Contenidos Internos Compartidos',
          latest: 'Lo más reciente',
          youMayLike: 'Podrían interesarte',
          recommendations: 'Recomendaciones',
          filteredContent: 'Contenido filtrado',
        };

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [continueWatching, setContinueWatching] = useState<
    ContinueWatchingItem[]
  >([]);

  const { categories: filterCategories } = useFilterCategories();

  const refreshContinueWatching = useCallback(() => {
    setContinueWatching(getContinueWatchingItems());
  }, []);

  const debounceSearchTerm = useCallback(
    debounce((param: string) => {
      setSearchTerm(param);
    }, 500),
    []
  );

  const { data, isFetching } = useQuery({
    queryKey: ['cardList'],
    queryFn: async () => {
      const { data } = await api.get(
        `${import.meta.env.VITE_API_URL}/recommendations/contents`
      );

      return data;
    },
  });

  const searchQuery = useQuery({
    queryKey: ['searchTerm', searchTerm],
    queryFn: async () => {
      if (searchTerm !== '') {
        const { data } = await api.get(
          `${
            import.meta.env.VITE_API_URL
          }/contents/contents_search/?query_param=${searchTerm}`
        );
        return data;
      }
      return null;
    },
    enabled: !!searchTerm,
  });

  const mandatoryQuery = useQuery({
    queryKey: ['mandatoryList'],
    queryFn: async () => {
      const { data } = await api.get(
        `${import.meta.env.VITE_API_URL}/contents/mandatory`
      );
      return data;
    },
  });

  const internalQuery = useQuery({
    queryKey: ['internalList'],
    queryFn: async () => {
      const { data } = await api.get(
        `${import.meta.env.VITE_API_URL}/contents/internal`
      );
      return data;
    },
  });

  const newContentsQuery = useQuery({
    queryKey: ['newList'],
    queryFn: async () => {
      const { data } = await api.get(
        `${import.meta.env.VITE_API_URL}/contents/new`
      );
      return data;
    },
  });

  const explorerQuery = useQuery({
    queryKey: ['explorerChannels'],
    queryFn: async () => {
      const { data } = await api.get(
        `${import.meta.env.VITE_API_URL}/contents/explorer`
      );
      return data;
    },
  });

  useEffect(() => {
    const currentSearch = searchParams.get('search') || '';
    const currentFilter = searchParams.get('filter') || '';

    if (currentSearch) {
      debounceSearchTerm(currentSearch);
      setSearchInput(currentSearch);
    }

    if (currentFilter) {
      setFilterTerm(currentFilter);
    }
  }, [debounceSearchTerm, searchParams]);

  useEffect(() => {
    refreshContinueWatching();

    window.addEventListener(
      YOUTUBE_CONTINUE_WATCHING_UPDATED,
      refreshContinueWatching
    );
    window.addEventListener('focus', refreshContinueWatching);

    return () => {
      window.removeEventListener(
        YOUTUBE_CONTINUE_WATCHING_UPDATED,
        refreshContinueWatching
      );
      window.removeEventListener('focus', refreshContinueWatching);
    };
  }, [refreshContinueWatching]);

  const handleSearch = (param: string) => {
    setSearchParams({ search: param });
    setSearchInput(param);
    debounceSearchTerm(param);
  };

  const handleFilter = (param: string) => {
    setSearchParams({ filter: param });
    setFilterTerm(param);
  };

  const handleRemoveContinueWatching = useCallback(
    (id: string | number) => {
      removeContinueWatchingItem(id);
      refreshContinueWatching();
    },
    [refreshContinueWatching]
  );

  const filterContent = (content: any) => {
    if (!filterTerm) return true;

    const selectedCategory = getFilterCategory(filterCategories, filterTerm);
    // Unknown key (stale ?filter= in the URL, or a category the backend
    // dropped): show everything rather than an empty grid.
    if (!selectedCategory) return true;

    return matchesFilterCategory(selectedCategory, content.capacity);
  };

  const values = flatMap(data, (item) => {
    if (Array.isArray(item.capacity_filter)) {
      return item.capacity_filter.map((capacity: any) => ({
        ...item,
        capacity: capacity.capacity,
      }));
    }

    return { ...item };
  });

  const browserContent = (
    <section className="container mx-auto px-3 lg:px-0">
      <StyledListContainer>
        <NewsTicker />

        <SearchBar
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          activeFilter={filterTerm}
          searchTerm={searchInput}
          categories={filterCategories}
        />

        {searchTerm === '' && filterTerm === '' && (
          <>
            {mandatoryQuery.data && mandatoryQuery.data.length > 0 && (
              <List
                data={mandatoryQuery.data}
                isFetching={mandatoryQuery.isFetching}
                title={copy.mandatory}
                handleFilter={handleFilter}
                showSeeAll={false}
              />
            )}

            {continueWatching.length > 0 && (
              <List
                data={continueWatching}
                isFetching={false}
                title={copy.continueWatching}
                handleFilter={handleFilter}
                showSeeAll={false}
                onRemoveItem={handleRemoveContinueWatching}
              />
            )}

            <List
              data={data}
              isFetching={isFetching}
              title={copy.aligned}
              handleFilter={handleFilter}
              showSeeAll={false}
            />

            {internalQuery.data && internalQuery.data.length > 0 && (
              <List
                data={internalQuery.data}
                isFetching={internalQuery.isFetching}
                title={copy.internal}
                handleFilter={handleFilter}
                showSeeAll={false}
              />
            )}

            {explorerQuery.data?.map((channel: any) => {
              if (!channel.contents || channel.contents.length === 0)
                return null;
              return (
                <List
                  key={channel.id}
                  data={channel.contents}
                  isFetching={explorerQuery.isFetching}
                  title={channel.name}
                  handleFilter={handleFilter}
                  showSeeAll={false}
                />
              );
            })}

            <List
              data={newContentsQuery.data}
              isFetching={newContentsQuery.isFetching}
              title={copy.latest}
              handleFilter={handleFilter}
              showSeeAll={false}
            />

            <Favorites contents={data} isFetching={isFetching} />

            <CardGrid
              id="card-grid"
              data={data}
              isFetching={isFetching}
              title={copy.youMayLike}
              handleFilter={handleFilter}
            />
          </>
        )}

        {searchTerm === '' && filterTerm === '' && (
          <CardGrid
            id="card-grid"
            data={data}
            isFetching={isFetching}
            title={copy.recommendations}
            handleFilter={handleFilter}
          />
        )}

        {searchTerm !== '' && (
          <CardGrid
            id="card-grid"
            data={searchQuery.data}
            isFetching={searchQuery.isFetching}
            searchParam={searchTerm}
          />
        )}

        {filterTerm !== '' && (
          <CardGrid
            id="card-grid"
            data={Array.from(
              new Set(
                values?.filter(filterContent).map((content) => content.id)
              )
            ).map((id) => values.find((content) => content.id === id))}
            isFetching={isFetching}
            filterParam={filterTerm}
            title={
              getCategoryLabel(
                getFilterCategory(filterCategories, filterTerm),
                language
              ) || copy.filteredContent
            }
          />
        )}
      </StyledListContainer>
    </section>
  );

  return withNavbar({ children: browserContent });
};

export default Browser;
